/**
 * MFTS bundled-shipping banner and "Bundles with this" badge UI.
 *
 * @todo (CHAIR-19843 / CHAIR-16489) Remove after experiment
 */

import { getSharedBundleShippingGroupKey, getSharedBundleShippingTypeCodes } from 'chairisher/context/product';

/**
 * Parcel shipping types share one bundle group. Other shipping types match themselves.
 *
 * @param {string|null|undefined} shippingTypeCode Product shipping type code
 * @returns {string|null} Parcel group key, the code for any other type, or null when there is no code
 */
function getBundleShippingGroupKey(shippingTypeCode) {
    return getSharedBundleShippingTypeCodes().includes(shippingTypeCode)
        ? getSharedBundleShippingGroupKey()
        : shippingTypeCode || null;
}

/**
 * @param {Object} productA First product JSON (`is_wholesale`, `shipping_type_code`)
 * @param {Object} productB Second product JSON (`is_wholesale`, `shipping_type_code`)
 * @returns {boolean}
 */
function areProductsBundleDisplayCompatible(productA, productB) {
    const hasSameWholesaleStatus = !!productA?.is_wholesale === !!productB?.is_wholesale;
    const groupA = getBundleShippingGroupKey(productA?.shipping_type_code);
    const groupB = getBundleShippingGroupKey(productB?.shipping_type_code);
    const hasSameShippingGroup = groupA !== null && groupA === groupB;

    return hasSameWholesaleStatus && hasSameShippingGroup;
}

/**
 * Inserts the bundled-shipping banner into an MFTS rail container.
 *
 * @param {Element} containerEl MFTS `.product-grid-container` element
 * @param {string} shopUrl Seller shop deep-link with bundle-shipping query params
 */
export function renderBundledShippingBanner(containerEl, shopUrl) {
    const scrollableGridEl = containerEl?.querySelector('.js-scrollable-grid');
    const bannerLinkEl = document
        .getElementById('js-mfts-bundled-shipping-banner-template')
        ?.content?.firstElementChild?.cloneNode(true);

    if (shopUrl && scrollableGridEl && bannerLinkEl && !containerEl.querySelector('.js-mfts-bundled-shipping-banner')) {
        bannerLinkEl.href = shopUrl;
        scrollableGridEl.before(bannerLinkEl);
    }
}

/**
 * Applies banner and badges from MFTS rail metadata.
 *
 * @param {Element} containerEl MFTS rail container
 * @param {Object} options Banner and badge inputs
 * @param {Object} options.pdpProduct PDP product JSON (`is_wholesale`, `shipping_type_code`)
 * @param {Object} options.responseData MFTS rail AJAX response
 */
export function applyBundledShippingUi(containerEl, { pdpProduct, responseData } = {}) {
    if (containerEl && responseData) {
        if (responseData.has_bundle_shipping_eligible_siblings && responseData.bundle_shipping_shop_url) {
            renderBundledShippingBanner(containerEl, responseData.bundle_shipping_shop_url);
        }

        const productIdToProductJson = responseData.product_id_to_product_json || {};

        if (pdpProduct) {
            containerEl.querySelectorAll('.js-product').forEach((productEl) => {
                const railProduct = productIdToProductJson[productEl.getAttribute('data-product-id')];
                const badgeEl = productEl.querySelector('.js-bundles-with-this-badge');
                const shouldShowBadge =
                    railProduct && areProductsBundleDisplayCompatible(pdpProduct, railProduct) && badgeEl;

                if (shouldShowBadge) {
                    badgeEl.classList.remove('hidden');
                }
            });
        }
    }
}
